/**
 * autoGrow — size a textarea to its content, up to a line limit, then let it scroll.
 *
 * A one-row field hides everything past the first line of a title somebody wrote in four; an
 * unbounded one pushes the rest of the panel off screen the moment a description gets long. So both
 * fields grow to fit and stop at a cap, and past it the field scrolls rather than the panel.
 *
 * Shared because the mission panel and the cell panel edit the same two fields and must not drift
 * apart — they sit one click from each other and read as one surface.
 */

/** A title is a phrase. Three lines is already generous; past that it is a description. */
export const TITLE_ROWS = 3;

/** A description is prose, and five lines is about what fits beside a graph without crowding it. */
export const DESCRIPTION_ROWS = 5;

/**
 * An answer to a question is prose too, and it is the thing the options did not cover — so it is
 * the field most likely to run long. Six is a paragraph; past it the FIELD scrolls, which is what
 * keeps a long answer from pushing the questions under it off the screen.
 */
export const ANSWER_ROWS = 6;

/**
 * @param {HTMLTextAreaElement} node
 * @param {number} maxRows  lines to grow to before scrolling
 */
export function autoGrow(node, maxRows) {
  if (!node || !node.style) return;
  // Collapse first: `scrollHeight` reports the CONTENT height only when the box is not already
  // taller than it, so measuring without this makes a shrinking field stay at its high-water mark.
  node.style.height = 'auto';

  const style = typeof window !== 'undefined' && window.getComputedStyle
    ? window.getComputedStyle(node) : null;
  const number = (value, fallback) => {
    const parsed = parseFloat(value);
    return Number.isFinite(parsed) ? parsed : fallback;
  };
  const fontSize = style ? number(style.fontSize, 16) : 16;
  // `line-height: normal` does not parse, and is roughly 1.2 — but Bootstrap sets 1.5 on controls,
  // so the fallback matches what these fields actually render at.
  const lineHeight = style ? number(style.lineHeight, fontSize * 1.5) : fontSize * 1.5;
  // Controls are `border-box`, so the cap has to include what the content box does not.
  const chrome = style
    ? number(style.paddingTop, 0) + number(style.paddingBottom, 0)
      + number(style.borderTopWidth, 0) + number(style.borderBottomWidth, 0)
    : 0;

  const cap = lineHeight * maxRows + chrome;
  const content = node.scrollHeight;

  // A node with no layout reports NOTHING, and writing that back collapses the field to a sliver it
  // can never grow out of: nothing re-measures until the value changes, and nobody can type into a
  // field four pixels tall. A modal is exactly this case — react-bootstrap mounts its body before
  // the dialog has been laid out, so the description field in New Mission / Add Cell measured zero
  // on mount and stayed there. The panels never hit it because a panel is laid out when it mounts.
  //
  // Leaving `height` unset is the honest answer: the `rows` attribute already describes the resting
  // size, and the next measurement — the first keystroke, or any re-render once the dialog is up —
  // lands with real numbers.
  if (!content) {
    node.style.height = '';
    node.style.overflowY = 'hidden';
    return;
  }

  const overflowing = content > cap;
  node.style.height = `${overflowing ? cap : content}px`;
  node.style.overflowY = overflowing ? 'auto' : 'hidden';
}

export default autoGrow;
